/**
 * Centralised query-key factory.
 *
 * Why: every TanStack Query mutation needs to invalidate sibling queries,
 * and chasing string literals across files is the #1 source of "why isn't
 * my list refetching?" bugs. We import keys from here, get autocompletion,
 * and refactors stay safe.
 *
 *   queryKey: qk.sites.list({ page: 1 })
 *   invalidateKey: qk.sites.all
 */

export const qk = {
  auth: {
    me: ["auth", "me"] as const,
  },

  sites: {
    all: ["sites"] as const,
    list: (params?: Record<string, unknown>) =>
      ["sites", "list", params ?? {}] as const,
    detail: (id: number) => ["sites", "detail", id] as const,
  },

  iot: {
    nodes: {
      all: ["iot", "nodes"] as const,
      list: (params?: Record<string, unknown>) =>
        ["iot", "nodes", "list", params ?? {}] as const,
      detail: (id: number) => ["iot", "nodes", "detail", id] as const,
    },
    managers: {
      all: ["iot", "managers"] as const,
      list: (params?: Record<string, unknown>) =>
        ["iot", "managers", "list", params ?? {}] as const,
    },
  },

  safety: {
    alerts: {
      all: ["safety", "alerts"] as const,
      list: (params?: Record<string, unknown>) =>
        ["safety", "alerts", "list", params ?? {}] as const,
      detail: (id: number) => ["safety", "alerts", "detail", id] as const,
    },
    risk: {
      all: ["safety", "risk"] as const,
      list: (params?: Record<string, unknown>) =>
        ["safety", "risk", "list", params ?? {}] as const,
    },
    anomaly: {
      all: ["safety", "anomaly"] as const,
      list: (params?: Record<string, unknown>) =>
        ["safety", "anomaly", "list", params ?? {}] as const,
      detail: (id: number) => ["safety", "anomaly", "detail", id] as const,
    },
    thresholds: {
      all: ["safety", "thresholds"] as const,
    },
    evacuation: {
      status: (siteId?: number) => ["safety", "evacuation", "status", siteId ?? 0] as const,
      assemblyPoints: (siteId?: number) =>
        ["safety", "evacuation", "assembly-points", siteId ?? 0] as const,
    },
    emergency: {
      all: ["safety", "emergency-dispatches"] as const,
      list: (params?: Record<string, unknown>) =>
        ["safety", "emergency-dispatches", "list", params ?? {}] as const,
    },
  },

  assets: {
    blueprints: {
      all: ["assets", "blueprints"] as const,
      list: (params?: Record<string, unknown>) =>
        ["assets", "blueprints", "list", params ?? {}] as const,
      detail: (id: number) => ["assets", "blueprints", "detail", id] as const,
    },
    cameras: {
      all: ["assets", "cameras"] as const,
      list: (params?: Record<string, unknown>) =>
        ["assets", "cameras", "list", params ?? {}] as const,
    },
    gasdb: ["assets", "gas-db"] as const,
  },

  capture: {
    all: ["capture", "scans"] as const,
    list: (params?: Record<string, unknown>) =>
      ["capture", "scans", "list", params ?? {}] as const,
    detail: (id: number) => ["capture", "scans", "detail", id] as const,
  },

  ops: {
    scenarios: {
      all: ["ops", "scenarios"] as const,
      list: (params?: Record<string, unknown>) =>
        ["ops", "scenarios", "list", params ?? {}] as const,
    },
    runs: {
      all: ["ops", "scenario-runs"] as const,
      list: (params?: Record<string, unknown>) =>
        ["ops", "scenario-runs", "list", params ?? {}] as const,
    },
    reports: {
      all: ["ops", "reports"] as const,
      list: (params?: Record<string, unknown>) =>
        ["ops", "reports", "list", params ?? {}] as const,
    },

    kpi: {
      all: ["ops", "kpi"] as const,
      summary: (params?: Record<string, unknown>) =>
        ["ops", "kpi", "summary", params ?? {}] as const,
      evidence: (params?: Record<string, unknown>) =>
        ["ops", "kpi", "evidence", params ?? {}] as const,
    },
    devices: {
      all: ["ops", "devices"] as const,
      list: (params?: Record<string, unknown>) =>
        ["ops", "devices", "list", params ?? {}] as const,
      detail: (id: number) => ["ops", "devices", "detail", id] as const,
      /** Choice labels served by the API itself (OPTIONS metadata). */
      choices: ["ops", "devices", "choices"] as const,
    },
    backup: ["ops", "backup", "status"] as const,
  },

  admin: {
    users: {
      all: ["admin", "users"] as const,
      list: (params?: Record<string, unknown>) =>
        ["admin", "users", "list", params ?? {}] as const,
    },
    orgs: {
      all: ["admin", "orgs"] as const,
      list: (params?: Record<string, unknown>) =>
        ["admin", "orgs", "list", params ?? {}] as const,
    },
  },
} as const;
